import { useSyncExternalStore } from "react";

// Tailwind-`md`-Breakpoint. Unterhalb davon kollabiert die TopNav in den
// Sheet-Drawer (Session 35 / J1). Wer den Wert aendert, sollte das
// gleichzeitig in `src/index.css` pruefen (falls dort eigene Media-Queries
// liegen) — im Projekt uebernimmt sonst Tailwind die Aufteilung.
// Exportiert, damit `tests/coverage/mobile-breakpoint.test.ts` (Session 37 /
// L2) die Konstante auf ihren Sollwert pruefen kann.
export const DESKTOP_QUERY = "(min-width: 768px)";

// Nav-eigener Breakpoint (POL-2, 2026-07-12; nachgemessen NAV-1, 2026-09-04).
// Die volle TopNav-Leiste traegt Logo + bis zu 6 Eintraege (Sandro: 4 direkt
// + «Finanzen»/«Verwaltung»-Dropdowns = 633 px; Jacqueline: 6 direkt = 612 px)
// + Suchfeld (192 px unter 2xl) + Feedback + Meldungs-Badge + Konto-Menue.
// Bei 1280 px bleiben Sandro 22 px und Jacqueline 17 px Luft zum Suchfeld;
// wird es enger (Sync-Spinner), kuerzt sich der Konto-Name per Ellipse, die
// Leiste selbst schrumpft nie (`shrink-0`). Darunter wuerde die Leiste
// ueberlaufen bzw. das Suchfeld die Reiter verdecken. Deshalb schalten wir bei
// 1280 px (Tailwind `xl`) auf den bestehenden Sheet-Drawer, der auf iPad quer
// (1024) / schmalem Fenster sauber alles traegt — dieselbe Struktur, Gruppen
// als Abschnitte.
// SEPARAT vom `DESKTOP_QUERY` (768 px): den hier NICHT mitverstellen, sonst
// kippen Tablets im 768–1279-Bereich in die Mobil-Kalkulation. Bei Layout-
// Aenderungen (Reiter-Zahl, Suchfeld-Breite) den Wert neu messen.
export const NAV_WIDE_QUERY = "(min-width: 1280px)";

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

function matchQuery(query: string): boolean {
  if (!isBrowser()) return true;
  return window.matchMedia(query).matches;
}

function getServerSnapshot(): boolean {
  // SSR/Node: wir rendern mit Desktop-Snapshot, damit der erste Client-
  // Paint auf Mobile die Mobile-Shell zeigt (Client-Snapshot laeuft
  // sofort nach Hydration). Fuer E2E ist der Wert irrelevant — Playwright
  // setzt den Viewport vor dem ersten Render.
  return true;
}

function subscribe(query: string): (onChange: () => void) => () => void {
  return (onChange) => {
    if (!isBrowser()) return () => undefined;
    const mql = window.matchMedia(query);
    // `change` gibt es auf allen modernen Engines; der alte Safari-Fallback
    // (`addListener`) ist seit v6 abgehaengt (React 19 braucht Safari 14+).
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  };
}

const subscribeDesktop = subscribe(DESKTOP_QUERY);
const subscribeNavWide = subscribe(NAV_WIDE_QUERY);

export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeDesktop,
    () => matchQuery(DESKTOP_QUERY),
    getServerSnapshot,
  );
}

export function useIsMobile(): boolean {
  return !useIsDesktop();
}

// Ist der Viewport breit genug fuer die volle TopNav-Leiste? Steuert allein die
// Leiste↔Drawer-Umschaltung (POL-2) — entkoppelt vom Kalkulations-Breakpoint.
export function useIsNavWide(): boolean {
  return useSyncExternalStore(
    subscribeNavWide,
    () => matchQuery(NAV_WIDE_QUERY),
    getServerSnapshot,
  );
}
